import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";

export const AuthErrorCode = {
	ACCOUNT_BLOCKED: "AUTH_ACCOUNT_BLOCKED",
	ACCOUNT_INACTIVE: "AUTH_ACCOUNT_INACTIVE",
	SESSION_REVOKED: "AUTH_SESSION_REVOKED",
	TOKEN_INVALID: "AUTH_TOKEN_INVALID",
	TOKEN_EXPIRED: "AUTH_TOKEN_EXPIRED",
	/** Permissions changed mid-session; refresh /me instead of logging out. */
	INSUFFICIENT_PERMISSION: "INSUFFICIENT_PERMISSION",
} as const;

export type AuthErrorCode = (typeof AuthErrorCode)[keyof typeof AuthErrorCode];

/** Survives hard redirect to /login after forceLogout. */
export const FORCE_LOGOUT_MESSAGE_KEY = "cms_portal_force_logout_message";

const FORCE_LOGOUT_CODES = new Set<string>([
	AuthErrorCode.ACCOUNT_BLOCKED,
	AuthErrorCode.ACCOUNT_INACTIVE,
	AuthErrorCode.SESSION_REVOKED,
	AuthErrorCode.TOKEN_INVALID,
]);

export interface ParsedApiError {
	message: string | null;
	code: string | null;
}

function readErrorData(
	error: FetchBaseQueryError | undefined,
): Record<string, unknown> | string | undefined {
	if (!error?.data) return undefined;
	return error.data as Record<string, unknown> | string;
}

export function extractApiErrorMessage(
	error: FetchBaseQueryError | undefined,
): string | null {
	const errorData = readErrorData(error);
	if (typeof errorData === "string" && errorData.trim()) {
		return errorData.trim();
	}
	if (errorData && typeof errorData === "object") {
		const message = errorData.message;
		if (typeof message === "string" && message.trim()) {
			return message.trim();
		}
		if (Array.isArray(message)) {
			const text = message
				.filter(
					(entry): entry is string =>
						typeof entry === "string" && entry.trim().length > 0,
				)
				.join(", ");
			if (text) return text;
		}
	}
	if (typeof error?.error === "string" && error.error.trim()) {
		return error.error.trim();
	}
	return null;
}

export function extractApiErrorCode(
	error: FetchBaseQueryError | undefined,
): string | null {
	const errorData = readErrorData(error);
	if (!errorData || typeof errorData !== "object") return null;
	const code = errorData.code;
	return typeof code === "string" && code.trim() ? code.trim() : null;
}

export function parseApiError(
	error: FetchBaseQueryError | undefined,
): ParsedApiError {
	return {
		message: extractApiErrorMessage(error),
		code: extractApiErrorCode(error),
	};
}

export function isForceLogoutAuthCode(code: string | null): boolean {
	return code !== null && FORCE_LOGOUT_CODES.has(code);
}

export function isInsufficientPermissionCode(code: string | null): boolean {
	return code === AuthErrorCode.INSUFFICIENT_PERMISSION;
}

export function isTokenExpiredAuthCode(code: string | null): boolean {
	return code === AuthErrorCode.TOKEN_EXPIRED;
}
